/**
 * Ficha de una **transacción del POS** de Izipay.
 *
 * Es la ventana `izipay/view.php` del panel anterior: todos los datos del cobro —el
 * terminal, la tarjeta, la autorización, el lote y el comercio—, el **código de
 * autorización** y su **respuesta completa del POS** (el JSON que guardó el
 * terminal, con su `printData`), el ticket para imprimir y la anulación, que sólo
 * ve un administrador.
 *
 * Ojo: anular aquí **no revierte el cargo** en la tarjeta; sólo deja la
 * transacción marcada, igual que el original.
 */

import { Ban, Printer, Receipt, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { cn, formatCurrency } from '@/lib/utils';

import { izipayApi } from '../izipay.api';
import type { DetalleIzipay } from '../izipay.types';

/** Tonos del distintivo según el resultado del cobro. */
const TONOS: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  success: 'success',
  partial: 'warning',
  failed: 'danger',
  reversed: 'neutral',
};

/** Claves que se destacan de la respuesta del POS. */
const CAMPOS_POS: [string, string][] = [
  ['ID de transacción', 'transactionId'],
  ['Código de respuesta', 'responseCod'],
  ['Mensaje', 'message'],
  ['Apoyo', 'errorCode'],
  ['Versión de la app', 'appVersion'],
  ['Cuotas', 'numInstallments'],
];

/** Un dato de la ficha, con su etiqueta. */
function Dato({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
        {etiqueta}
      </span>
      <span className="text-sm break-words text-ink-800">{valor}</span>
    </div>
  );
}

interface DialogoTransaccionProps {
  transaccionId: number | null;
  consulta: DetalleIzipay | undefined;
  cargando: boolean;
  guardando: boolean;
  error: string | null;
  onCerrar: () => void;
  onAnular: (id: number, etiqueta: string) => void;
}

/** Ficha de la transacción, con su respuesta del POS. */
export function DialogoTransaccionIzipay({
  transaccionId,
  consulta,
  cargando,
  guardando,
  error,
  onCerrar,
  onAnular,
}: DialogoTransaccionProps) {
  const transaccion = consulta?.transaccion;
  const [verRespuesta, setVerRespuesta] = useState(false);
  const [errorTicket, setErrorTicket] = useState<string | null>(null);

  const abrirTicket = async () => {
    if (!transaccionId) return;

    setErrorTicket(null);

    try {
      await abrirPdfAutenticado(izipayApi.urlComprobante(transaccionId), {
        nombre: `izipay-${transaccionId}.pdf`,
      });
    } catch (fallo) {
      setErrorTicket(fallo instanceof Error ? fallo.message : 'No se pudo abrir el comprobante.');
    }
  };

  const respuesta = transaccion?.respuesta ?? null;

  return (
    <Dialog
      open={Boolean(transaccionId)}
      onClose={onCerrar}
      title={transaccionId ? `Transacción #${transaccionId}` : 'Transacción'}
      description={
        transaccion
          ? `${transaccion.metodo} · S/ ${transaccion.monto.toFixed(2)} · ${transaccion.registrado ?? ''}`
          : undefined
      }
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <div>
            {consulta?.puede_anular && transaccion ? (
              <Button
                variant="danger"
                size="sm"
                leftIcon={<Ban className="size-3.5" aria-hidden="true" />}
                disabled={guardando}
                onClick={() =>
                  onAnular(
                    transaccion.id,
                    transaccion.referencia
                      ? `${transaccion.referencia} (S/ ${transaccion.monto.toFixed(2)})`
                      : `#${transaccion.id}`,
                  )
                }
              >
                Anular transacción
              </Button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {transaccion ? (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Printer className="size-3.5" aria-hidden="true" />}
                onClick={() => void abrirTicket()}
              >
                Ticket
              </Button>
            ) : null}

            <Button variant="outline" size="sm" onClick={onCerrar}>
              Cerrar
            </Button>
          </div>
        </div>
      }
    >
      {cargando ? (
        <div className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {error ? (
            <Alert variant="error" title="No se pudo completar la acción">
              {error}
            </Alert>
          ) : null}

          {errorTicket ? (
            <Alert variant="error" title="No se pudo abrir el ticket">
              {errorTicket}
            </Alert>
          ) : null}

          {transaccion?.anulado ? (
            <Alert variant="warning" title="Transacción anulada">
              La anuló {transaccion.anulado_por ?? '—'}
              {transaccion.anulado_el ? ` el ${transaccion.anulado_el}` : ''}
              {transaccion.dispositivo ? ` desde ${transaccion.dispositivo}` : ''}. El cargo no se
              revierte en la tarjeta: sólo queda marcado aquí.
            </Alert>
          ) : null}

          {transaccion ? (
            <>
              <section className="rounded-card border border-ink-100 p-4">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <Dato
                    etiqueta="Monto"
                    valor={
                      <span
                        className={cn(
                          'font-bold tabular-nums',
                          transaccion.anulado ? 'text-ink-400 line-through' : 'text-emerald-700',
                        )}
                      >
                        {formatCurrency(transaccion.monto)}
                      </span>
                    }
                  />
                  <Dato
                    etiqueta="Estado"
                    valor={
                      transaccion.anulado ? (
                        <Badge variant="danger">Anulada</Badge>
                      ) : (
                        <Badge variant={TONOS[transaccion.estado] ?? 'neutral'}>
                          {transaccion.estado_etiqueta}
                        </Badge>
                      )
                    }
                  />
                  <Dato etiqueta="Método" valor={transaccion.metodo} />
                  <Dato etiqueta="Registrada" valor={transaccion.registrado ?? '—'} />
                  <Dato etiqueta="Procesada" valor={transaccion.procesado ?? '—'} />
                  <Dato etiqueta="POS" valor={transaccion.pos} />
                  <Dato etiqueta="Terminal" valor={transaccion.terminal ?? '—'} />
                  <Dato etiqueta="Comercio" valor={transaccion.comercio ?? '—'} />
                  <Dato etiqueta="Lote" valor={transaccion.lote ?? '—'} />
                  <Dato etiqueta="Referencia" valor={transaccion.referencia ?? '—'} />
                  <Dato
                    etiqueta="Tarjeta"
                    valor={transaccion.ultimos_digitos ? `***${transaccion.ultimos_digitos}` : '—'}
                  />
                  <Dato etiqueta="Marca" valor={transaccion.marca ?? '—'} />
                  <Dato etiqueta="Autorización" valor={transaccion.autorizacion ?? '—'} />
                  <Dato etiqueta="Tipo de lectura" valor={transaccion.lectura ?? '—'} />
                  <Dato
                    etiqueta="Turno"
                    valor={transaccion.turno_id ? `#${transaccion.turno_id}` : '—'}
                  />
                </div>
              </section>

              {respuesta ? (
                <section className="rounded-card border border-ink-100 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-800">
                      <Receipt className="size-4 text-ink-400" aria-hidden="true" />
                      Respuesta del POS
                    </h3>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setVerRespuesta((actual) => !actual)}
                    >
                      {verRespuesta ? 'Ocultar' : 'Ver respuesta completa'}
                    </Button>
                  </div>

                  <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {CAMPOS_POS.map(([etiqueta, clave]) => (
                      <Dato
                        key={clave}
                        etiqueta={etiqueta}
                        valor={
                          respuesta[clave] === undefined || respuesta[clave] === null
                            ? '—'
                            : String(respuesta[clave]).replace(/^"|"$/g, '')
                        }
                      />
                    ))}
                  </div>

                  {verRespuesta ? (
                    <pre className="mt-3 max-h-64 overflow-auto rounded-control bg-ink-50 p-3 text-[11px] leading-relaxed text-ink-700">
                      {JSON.stringify(respuesta, null, 2)}
                    </pre>
                  ) : null}
                </section>
              ) : null}

              <p className={cn('flex items-start gap-2 text-xs text-ink-500')}>
                <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-amber-500" aria-hidden="true" />
                La anulación deja la transacción marcada para que no se cuente dos veces; el cargo
                en la tarjeta se revierte desde el terminal de Izipay.
              </p>
            </>
          ) : (
            <p className="text-sm text-ink-500">No se ha podido leer la transacción.</p>
          )}
        </div>
      )}
    </Dialog>
  );
}
